import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Clock, Mail, Wand2 } from "lucide-react";
import { brandAssets } from "@/lib/brand/tokens";
import { getSetupFlow } from "@/data/setup/registry";

const SetupCompletePage = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const flow = getSetupFlow(slug);
  const firstName = (searchParams.get("n") || "").split(" ")[0];
  const email = searchParams.get("e");
  const image = flow?.intro.image;
  const flowName = flow?.meta.name || "genie";

  const steps = [
    {
      icon: Wand2,
      title: "We're building your genie",
      body: `Our team is setting up your ${flowName} front desk from the details you shared.`,
    },
    {
      icon: Clock,
      title: "Usually within a few hours",
      body: "Most genies are ready the same day — often much sooner.",
    },
    {
      icon: Mail,
      title: "We'll email you the moment it's live",
      body: email ? `Keep an eye on ${email} for your genie and its link.` : "Watch your inbox for your genie and its link.",
    },
  ];

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#0B1224]">
      {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      <div className="absolute inset-0" style={{ background: DONE_SCRIM }} />
      <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: RIBBON }} />

      <img
        src={brandAssets.wordmark}
        alt="Help Genie"
        className="absolute left-6 top-6 z-10 h-8 w-auto sm:left-12 sm:top-10 sm:h-10"
        style={{ filter: "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.45))" }}
      />

      <div className="absolute inset-0 z-10 flex items-center overflow-y-auto">
        <div className="w-full max-w-2xl px-6 py-16 sm:px-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 py-1.5 pl-1.5 pr-3.5 backdrop-blur">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">Received</span>
          </div>

          <h1
            className="mt-5 text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl"
            style={{ textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
          >
            {firstName ? `Thanks, ${firstName} — we're building it.` : "We're on it."}
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg">
            Your details are in. We're bringing your {flowName.toLowerCase()} front desk to life, and
            we'll email you the moment it's ready to try.
          </p>

          {/* Frosted next-steps card */}
          <div className="mt-7 overflow-hidden rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md">
            {steps.map((step, index) => (
              <div
                key={step.title}
                className={`flex items-start gap-4 p-4 sm:p-5 ${
                  index < steps.length - 1 ? "border-b border-white/15" : ""
                }`}
              >
                <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                  <step.icon className="h-5 w-5 text-white" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{step.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-white/75 sm:text-sm">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href="https://helpgenie.ai"
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105 hover:opacity-95"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              Explore Help Genie
              <ArrowRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => navigate("/setup")}
              className="text-sm font-medium text-white/75 underline-offset-4 hover:text-white hover:underline"
            >
              Set up another genie
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RIBBON = "linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%)";

const DONE_SCRIM =
  "linear-gradient(120deg, rgba(11,18,36,0.90) 0%, rgba(11,18,36,0.62) 48%, rgba(11,18,36,0.25) 100%), linear-gradient(180deg, rgba(11,18,36,0.35) 0%, rgba(11,18,36,0.55) 100%)";

export default SetupCompletePage;
